/**
 * GConfidence — three-level confidence badge (STORY-000-006a).
 *
 * Used by AI-derived data surfaces (parsed quote line items,
 * extracted material rows, RAG citations) to communicate model
 * confidence at a glance. The dot color carries the meaning; the
 * label spells it out so colour alone never determines comprehension
 * (WCAG AA per `docs/GardEn/DESIGN-SYSTEM.md` translation rules).
 *
 * Token mapping (light + dark via theme.css):
 *   high   -> --g-success  (green)
 *   medium -> --g-warn     (amber)
 *   low    -> --g-error    (red)
 *
 * Visual reference: docs/GardEn/src/ui.jsx `GConfidence` (prototype
 * uses `high|med|low`; story renames `med` → `medium`).
 */
import type { ReactElement } from "react";

export type GConfidenceLevel = "low" | "medium" | "high";

export interface GConfidenceProps {
  /** Confidence level. Defaults to `medium`. */
  level?: GConfidenceLevel;
  /** Localised label shown alongside the indicator dot. The caller
   * is responsible for translation (react-i18next at the call site). */
  label: string;
  /** Additional classes for the pill wrapper. */
  className?: string;
}

/**
 * Tailwind utility classes per level. The dot uses the semantic
 * `bg-*` mapping defined in theme.css; the pill itself uses a
 * tinted background variant. Using arbitrary-value Tailwind
 * utilities (`bg-[var(--g-success)]/10` style) is intentionally
 * avoided here so this primitive stays readable; we instead rely
 * on the `bg-muted/...` shadcn pattern that already resolves via
 * the theme.
 */
const levelToToneClass: Record<
  GConfidenceLevel,
  { pillBg: string; dotBg: string; tone: string }
> = {
  high: {
    pillBg: "bg-[var(--g-success)]/10",
    dotBg: "bg-[var(--g-success)]",
    tone: "text-[var(--g-success)]",
  },
  medium: {
    pillBg: "bg-[var(--g-warn)]/10",
    dotBg: "bg-[var(--g-warn)]",
    tone: "text-[var(--g-warn)]",
  },
  low: {
    pillBg: "bg-[var(--g-error)]/10",
    dotBg: "bg-[var(--g-error)]",
    tone: "text-[var(--g-error)]",
  },
};

/**
 * Renders a pill badge with a colored dot and label.
 *
 * The `level` controls the dot + tinted-background tone via the
 * GardEn semantic tokens (`--g-success`/`--g-warn`/`--g-error`).
 * The `label` text is rendered verbatim — i18n is the caller's
 * responsibility.
 */
export function GConfidence({
  level = "medium",
  label,
  className,
}: GConfidenceProps): ReactElement {
  const tone = levelToToneClass[level];
  const classes = [
    "inline-flex",
    "items-center",
    "gap-1.5",
    "rounded-full",
    "px-2.5",
    "py-1",
    "text-xs",
    "font-medium",
    tone.pillBg,
    tone.tone,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      data-testid="garden-confidence"
      data-level={level}
      className={classes}
    >
      <span
        aria-hidden="true"
        data-testid="garden-confidence-dot"
        className={`block h-2 w-2 rounded-full ${tone.dotBg}`}
      />
      <span>{label}</span>
    </span>
  );
}

/**
 * `ConfidenceBadge` — design-revamp alias for {@link GConfidence}. The
 * handoff-2026-06 spec names this primitive `ConfidenceBadge`; existing
 * consumers keep importing `GConfidence`.
 */
export { GConfidence as ConfidenceBadge };
